// ErrorModal.tsx
import React, { useEffect } from 'react';
import { createRoot, Root } from 'react-dom/client';
import { AlertCircle, X } from 'lucide-react';

interface ErrorModalProps {
  message: string;
  onClose: () => void;
}

const ErrorModal: React.FC<ErrorModalProps> = ({ message, onClose }) => {
  useEffect(() => {
    // Prevent body scroll when modal is open
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, []);

  return (
    <div className="fixed inset-0 flex items-center justify-center z-[9999] bg-black bg-opacity-50">
      <div className="bg-white shadow-xl rounded-lg p-6 flex flex-col gap-4 w-96 max-w-md mx-4">
        <div className="flex items-start gap-3">
          <div className="flex-shrink-0">
            <AlertCircle className="w-6 h-6 text-red-500" />
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Error</h3>
            <p className="text-gray-700 text-sm">{message}</p>
          </div>
          <button
            onClick={onClose}
            className="flex-shrink-0 text-gray-400 hover:text-gray-600 transition-colors"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm rounded-md bg-red-500 text-white hover:bg-red-600 transition-colors"
          >
            OK
          </button>
        </div>
      </div>
    </div>
  );
};

// Global state to manage modals
let modalContainer: HTMLDivElement | null = null;
let root: Root | null = null;

const createModalContainer = () => {
  if (!modalContainer) {
    modalContainer = document.createElement('div');
    modalContainer.id = 'error-modal-root';
    modalContainer.style.position = 'fixed';
    modalContainer.style.top = '0';
    modalContainer.style.left = '0';
    modalContainer.style.width = '100%';
    modalContainer.style.height = '100%';
    modalContainer.style.zIndex = '9999';
    document.body.appendChild(modalContainer);
  }
  return modalContainer;
};

const cleanupModal = () => {
  if (root) {
    root.unmount();
    root = null;
  }
  if (modalContainer && modalContainer.parentNode) {
    modalContainer.parentNode.removeChild(modalContainer);
    modalContainer = null;
  }
};

export function errorModal(message: string) {
  const container = createModalContainer();
  
  const handleClose = () => {
    cleanupModal();
  };

  // Create root and render the modal
  if (!root) {
    root = createRoot(container);
  }
  
  root.render(
    <ErrorModal 
      message={message} 
      onClose={handleClose} 
    />
  );
}

